import { useQuery, useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useCurrentUserGrants } from "../../../hooks/useCurrentUserGrants";
import { useUrlSort } from "../../../hooks/useUrlSort";
import { spaceIcon } from "../../../lib/spaceIcon";
import { getWritableScopeIds } from "../../../lib/getWritableScopeIds";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Dropdown } from "../../Dropdown";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { ListTable } from "../../ListTable";
import { ServiceAccountAvatar } from "../../ServiceAccountAvatar";
import { Text } from "../../Text";
import { Toggle } from "../../Toggle";
import { OverflowBadges } from "../OverflowBadges";
import {
  ClickablePillBadge,
  HoverActions,
  PillBadge,
  RowNameContent,
  RowNameLayout,
  ScrollArea,
} from "../shared";

const EmptyCell = () => (
  <Text variant="text-small" color="var(--text-secondary)">
    —
  </Text>
);

export function ServiceAccountsSettings() {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [accounts, result] = useQuery(queries.serviceAccounts());
  const [myGrants] = useCurrentUserGrants();
  const manageableIds = getWritableScopeIds(
    myGrants,
    accounts.map((account) => account.id),
    "service_account:manage"
  );
  const { q } = useCurrentUrl(routes.settings.members.serviceAccounts);
  const search = q ?? "";
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.settings.members.serviceAccounts,
    { name: "asc" }
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return accounts;
    return accounts.filter((a) => a.name.toLowerCase().includes(needle));
  }, [accounts, search]);

  const items = useMemo(
    () =>
      filtered.map((account) => {
        // A service account can hold several roles in one space; show each
        // space once.
        const seenSpace = new Set<string>();
        const spaces = account.roleGrantsAsPrincipal.flatMap((g) => {
          const space = g.scopeAsSpace;
          if (!space || seenSpace.has(space.id)) return [];
          seenSpace.add(space.id);
          return [space];
        });
        // Many federations can point at one provider; show each provider once.
        const seenProvider = new Set<string>();
        const providers = account.federations.flatMap((f) => {
          const provider = f.provider;
          if (!provider || seenProvider.has(provider.id)) return [];
          seenProvider.add(provider.id);
          return [provider];
        });

        return {
          key: account.id,
          link: makeUrl(routes.modal.serviceAccount, { id: account.id }),
          name: (
            <RowNameLayout>
              <ServiceAccountAvatar seed={account.id} />
              <RowNameContent>
                <Text variant="text-regular-md" ellipsis>
                  {account.name}
                </Text>
                <Text
                  variant="text-small"
                  color="var(--text-secondary)"
                  ellipsis
                >
                  Created {new Date(account.createdAt).toLocaleDateString()}
                </Text>
              </RowNameContent>
            </RowNameLayout>
          ),
          spaces:
            spaces.length > 0 ? (
              <OverflowBadges
                items={spaces}
                keyFn={(s) => s.id}
                renderBadge={(s) => (
                  <ClickablePillBadge
                    icon={spaceIcon(s.mode)}
                    style={{ flexShrink: 0 }}
                    onClick={() => navigate(routes.modal.space, { id: s.id })}
                  >
                    {s.name}
                  </ClickablePillBadge>
                )}
                overflowTooltip={(hidden) =>
                  hidden.map((s) => s.name).join(", ")
                }
              />
            ) : (
              <EmptyCell />
            ),
          apiKeys:
            account.apiKeys.length > 0 ? (
              <OverflowBadges
                items={account.apiKeys}
                keyFn={(k) => k.id}
                renderBadge={(k) => (
                  <PillBadge icon="key" style={{ flexShrink: 0 }}>
                    {k.name ?? "Key"}
                  </PillBadge>
                )}
                overflowTooltip={(hidden) =>
                  hidden.map((k) => k.name ?? "Key").join(", ")
                }
              />
            ) : (
              <EmptyCell />
            ),
          federations:
            providers.length > 0 ? (
              <OverflowBadges
                items={providers}
                keyFn={(p) => p.id}
                renderBadge={(p) => (
                  <PillBadge icon="id-card-lanyard" style={{ flexShrink: 0 }}>
                    {p.name}
                  </PillBadge>
                )}
                overflowTooltip={(hidden) =>
                  hidden.map((p) => p.name).join(", ")
                }
              />
            ) : (
              <EmptyCell />
            ),
          enabled: (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Toggle
                isOn={!account.disabled}
                disabled={!manageableIds.has(account.id)}
                onToggle={() =>
                  z.mutate(
                    sharedMutators.updateServiceAccount({
                      id: account.id,
                      disabled: !account.disabled,
                    })
                  )
                }
              />
            </div>
          ),
          actions: (hasMouse: boolean) =>
            manageableIds.has(account.id) ? (
              <HoverActions visible={hasMouse}>
                <Dropdown
                  opener={<Button variant="secondary" icon="more" />}
                  align="bottom"
                  minWidth={160}
                  sections={[
                    {
                      items: [
                        {
                          type: "button" as const,
                          left: <Icon name="trash" size={16} />,
                          label: "Delete",
                          onClick: () =>
                            setConfirmDelete({
                              id: account.id,
                              name: account.name,
                            }),
                        },
                      ],
                    },
                  ]}
                />
              </HoverActions>
            ) : null,
        };
      }),
    [filtered, z, makeUrl, manageableIds]
  );

  return (
    <>
      <ScrollArea>
        {accounts.length === 0 ? (
          result.type === "complete" ? (
            <EmptyState
              iconName="bot"
              title="No service accounts yet"
              description="Service accounts are identities for the scripts, CI jobs, and services you run — not people. Each one gets its own permissions and keys, so an automation can act on its own."
            />
          ) : null
        ) : (
          <ListTable
            headers={{
              name: "Name",
              spaces: "Spaces",
              apiKeys: "API keys",
              federations: "Federations",
              enabled: "Enabled",
              actions: "",
            }}
            columns={{
              spaces: "md",
              apiKeys: "md",
              federations: "md",
              enabled: "content",
            }}
            sortableColumns={sortableColumns}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            items={items}
            emptyState={
              <EmptyState
                iconName="bot"
                title="No matches"
                description="Try a different search."
              />
            }
          />
        )}
      </ScrollArea>

      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            z.mutate(
              sharedMutators.deleteServiceAccount({ id: confirmDelete.id })
            );
            setConfirmDelete(null);
          }}
          title="Delete service account"
          description={`Deleting “${confirmDelete.name}” immediately revokes its API keys, federations, and access. This can’t be undone.`}
          confirmLabel="Delete"
          destructive
        />
      )}
    </>
  );
}
